/*
 * MCRO001-F0050 / T-MCRO-050 (design: choreo MCRO001-D004 §4.1.3 render registry)
 * Slice 2 of 3 — the client render registry: `kind -> renderer`.
 *
 * This is THE extension mechanism. Adding a new box type is ONE entry here plus its
 * renderer component — never a grammar, node-schema or merge-engine change. The map is
 * a PLAIN object with NO choreography imports, so it stays generically reusable.
 *
 * Lookup rule: `getRenderer(kind)` returns the registered renderer, or UnknownKindBox
 * when the kind is unregistered. An unknown kind is NOT an error — it degrades to a
 * plain labelled box (§4.3 unknown-kind rule), so an older client meeting a newer kind
 * never breaks.
 *
 * Registration is one entry per kind. `registerRenderer` exists for a future kind to
 * register out-of-band without editing this file, but the well-known kinds are wired
 * here at module load.
 */

import ConflictPicker from '../../components/annotation/ConflictPicker';
import SuggestionPicker from '../../components/annotation/SuggestionPicker';
import CommentViewer from '../../components/annotation/CommentViewer';
import CompareViewer from '../../components/annotation/CompareViewer';
import UnknownKindBox from '../../components/annotation/UnknownKindBox';

/**
 * The well-known kind -> renderer map. A plain object: one entry per kind.
 * @type {Record<string, React.ComponentType<any>>}
 */
export const ANNOTATION_REGISTRY = {
  conflict: ConflictPicker,
  suggest: SuggestionPicker,
  comment: CommentViewer,
  compare: CompareViewer,
};

/** The graceful-degrade fallback for any unregistered kind. */
export { UnknownKindBox };

/**
 * Resolve a renderer for `kind`. Falls back to UnknownKindBox for any unregistered kind.
 * @param {string} kind
 * @returns {React.ComponentType<any>}
 */
export function getRenderer(kind) {
  return ANNOTATION_REGISTRY[kind] ?? UnknownKindBox;
}

/**
 * Register a renderer for a kind (the one-line extension point). Throwing on an empty
 * kind keeps the registry keys meaningful; re-registering an existing kind replaces it.
 * @param {string} kind
 * @param {React.ComponentType<any>} renderer
 */
export function registerRenderer(kind, renderer) {
  if (typeof kind !== 'string' || kind.length === 0) {
    throw new Error('registerRenderer: `kind` must be a non-empty string.');
  }
  if (typeof renderer !== 'function' && (typeof renderer !== 'object' || renderer === null)) {
    throw new Error('registerRenderer: `renderer` must be a React component.');
  }
  ANNOTATION_REGISTRY[kind] = renderer;
}
